import { useState, useEffect, useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Save, CheckCircle, Upload, Rocket, ImageIcon, Loader2 } from 'lucide-react'
import { TattooImageGallery } from './TattooImageGallery'
import { FlashGallery } from './FlashGallery'
import { generateOnboardingCode } from '@/lib/codeGenerator'
import { uploadImageToStorageSecure } from '@/lib/secureFileUpload'
import { BannerImageCropper, type CropData } from '@/components/shared/BannerImageCropper'
import { AddressAutocomplete, type AddressSearchResult } from '@/components/shared/AddressAutocomplete'
import type { Artist, ArtistOnboarding } from '@/types/models'
import { InvitingArtistAttribution } from '@/components/admin/InvitingArtistPickerDialog'

interface OnboardingDetailViewProps {
  onboarding: ArtistOnboarding
  loading?: boolean
  onBack: () => void
  onSave: (updates: Partial<ArtistOnboarding>) => Promise<void>
  onMarkReady: (updates: Partial<ArtistOnboarding>) => Promise<void>
  onPublishArtist?: (updates: Partial<ArtistOnboarding>) => Promise<void>
  /** Resolves `invitingArtist` id to profile (e.g. ArtistsDataContext.allArtists) */
  artistsForLookup?: Artist[]
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'scraper_started':
      return 'bg-orange-100 text-orange-800'
    case 'scraper_done':
      return 'bg-yellow-100 text-yellow-800'
    case 'ready_for_artist':
      return 'bg-blue-100 text-blue-800'
    case 'waiting_for_review':
      return 'bg-purple-100 text-purple-800'
    case 'completed':
      return 'bg-green-100 text-green-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const formatStatus = (status: string) => {
  switch (status) {
    case 'scraper_started':
      return 'Scraper Started'
    case 'scraper_done':
      return 'Scraper Done'
    case 'ready_for_artist':
      return 'Ready for Artist'
    case 'waiting_for_review':
      return 'Waiting for Review'
    case 'completed':
    case 'done':
      return 'Completed'
    default:
      return status
  }
}

export function OnboardingDetailView({
  onboarding,
  loading = false,
  onBack,
  onSave,
  onMarkReady,
  onPublishArtist,
  artistsForLookup = [],
}: OnboardingDetailViewProps) {
  const [editedData, setEditedData] = useState<Partial<ArtistOnboarding>>({
    displayName: onboarding.displayName,
    firstName: onboarding.firstName || '',
    lastName: onboarding.lastName || '',
    bio: onboarding.bio,
    location: onboarding.location || '',
    lat: onboarding.lat,
    lon: onboarding.lon,
    profileImageUrl: onboarding.profileImageUrl,
    backdropUrl: onboarding.backdropUrl || '',
    tattooImageUrls: onboarding.tattooImageUrls || [],
    flashes: onboarding.flashes || []
  })
  const [selectedFlashIds, setSelectedFlashIds] = useState<string[]>(
    onboarding.flashes?.map(flash => flash.id) || []
  )
  const [saving, setSaving] = useState(false)
  const [publishProgressOpen, setPublishProgressOpen] = useState(false)
  const [publishElapsedSec, setPublishElapsedSec] = useState(0)
  const [publishError, setPublishError] = useState<string | null>(null)
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null)
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null)
  const [profileUploading, setProfileUploading] = useState(false)
  const [profileUploadError, setProfileUploadError] = useState<string | null>(null)
  const [bannerImageFile, setBannerImageFile] = useState<File | null>(null)
  const [bannerImagePreview, setBannerImagePreview] = useState<string | null>(null)
  const [bannerUploading, setBannerUploading] = useState(false)
  const [bannerUploadError, setBannerUploadError] = useState<string | null>(null)
  const [showBannerCropper, setShowBannerCropper] = useState(false)
  const [geocodingError, setGeocodingError] = useState<string | null>(null)

  useEffect(() => {
    if (!publishProgressOpen) return
    setPublishElapsedSec(0)
    const id = window.setInterval(() => {
      setPublishElapsedSec((s) => s + 1)
    }, 1000)
    return () => window.clearInterval(id)
  }, [publishProgressOpen])

  const publishStatusMessage = useMemo(() => {
    if (publishElapsedSec < 5) {
      return 'Saving your latest edits to the database…'
    }
    if (publishElapsedSec < 120) {
      return 'The server is creating the artist profile, running portfolio & flash embeddings, regenerating search data, and compressing images. This often takes 2–10 minutes—please keep this tab open.'
    }
    return 'Still working. Large portfolios take longer—please wait and do not refresh the page.'
  }, [publishElapsedSec])

  const publishElapsedLabel = useMemo(() => {
    const m = Math.floor(publishElapsedSec / 60)
    const s = publishElapsedSec % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }, [publishElapsedSec])

  const handleInputChange = (field: keyof ArtistOnboarding, value: string) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleTattooSelectionChange = (selectedUrls: string[]) => {
    setEditedData(prev => ({
      ...prev,
      tattooImageUrls: selectedUrls
    }))
  }

  const handleFlashSelectionChange = (selectedIds: string[]) => {
    setSelectedFlashIds(selectedIds)
    // Update the flashes array to only include selected flashes
    const selectedFlashes = onboarding.flashes?.filter(flash => selectedIds.includes(flash.id)) || []
    setEditedData(prev => ({
      ...prev,
      flashes: selectedFlashes
    }))
  }

  const handleProfileImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setProfileUploadError(null)
    setProfileUploading(true)
    setProfileImageFile(file)

    try {
      // Create preview with FileReader for immediate display
      const reader = new FileReader()
      reader.onload = (e) => {
        const result = e.target?.result as string
        setProfileImagePreview(result)
      }
      reader.readAsDataURL(file)

      // Upload to Firebase Storage using onboardings path
      const onboardingIdentifier = onboarding.id || onboarding.userId || onboarding.displayName
      const firebaseUrl = await uploadImageToStorageSecure(
        file,
        `onboardings/${onboardingIdentifier}/profile`,
        onboardingIdentifier
      )
      
      // Update edited data with Firebase URL (not the data URL!)
      setEditedData(prev => ({
        ...prev,
        profileImageUrl: firebaseUrl
      }))
    } catch (error) {
      console.error('Failed to upload profile image:', error)
      setProfileUploadError('Failed to upload profile image. Please try again.')
      setProfileImageFile(null)
      setProfileImagePreview(null)
    } finally {
      setProfileUploading(false)
    }
  }

  const handleBannerImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setBannerUploadError(null)
    setBannerImageFile(file)
    setShowBannerCropper(true)
  }

  const handleBannerCropComplete = async (croppedImageUrl: string, _cropData: CropData) => {
    setBannerUploading(true)
    setShowBannerCropper(false)

    try {
      // Convert the cropped image URL to a File for upload
      const response = await fetch(croppedImageUrl)
      const blob = await response.blob()
      const croppedFile = new File([blob], `banner-${Date.now()}.jpg`, { type: 'image/jpeg' })

      // Upload to Firebase Storage using onboardings path (allows unauthenticated uploads)
      const onboardingIdentifier = onboarding.id || onboarding.userId || onboarding.displayName
      const firebaseUrl = await uploadImageToStorageSecure(
        croppedFile,
        `onboardings/${onboardingIdentifier}/banner`,
        onboardingIdentifier
      )
      
      // Update form data with Firebase URL
      setEditedData(prev => ({
        ...prev,
        backdropUrl: firebaseUrl
      }))
      setBannerImagePreview(croppedImageUrl)
    } catch (error) {
      console.error('Failed to upload banner image:', error)
      setBannerUploadError('Failed to upload banner image. Please try again.')
      setBannerImageFile(null)
      setBannerImagePreview(null)
    } finally {
      setBannerUploading(false)
    }
  }

  const handleBannerCropCancel = () => {
    setShowBannerCropper(false)
    setBannerImageFile(null)
    setBannerUploadError(null)
  }

  const handleAddressSelect = (place: AddressSearchResult) => {
    setEditedData(prev => ({
      ...prev,
      location: place.address,
      lat: place.lat,
      lon: place.lon
    }))
    setGeocodingError(null)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const updates = { ...editedData }
      
      // Profile image upload is now handled immediately in handleProfileImageChange
      // The firebaseUrl is already stored in editedData.profileImageUrl

      await onSave(updates)
    } catch (error) {
      console.error('Error saving onboarding:', error)
      // TODO: Show error toast
    } finally {
      setSaving(false)
    }
  }

  const handleMarkReady = async () => {
    setSaving(true)
    try {
      // Generate a unique 4-character code if not already present
      const code = onboarding.code || generateOnboardingCode()
      
      // Filter postUrls to only include the images that were selected by admin
      const selectedInstagramUrls = editedData.tattooImageUrls?.filter(url => 
        onboarding.postUrls?.includes(url)
      ) || []
      
      const updates = {
        ...editedData,
        status: 'ready_for_artist' as const,
        code: code,
        currentStep: 1, // Initialize to step 1 for artist flow
        postUrls: selectedInstagramUrls // Only keep admin-selected Instagram images
      }

      await onMarkReady(updates)
      
      // Navigate back to list view after successful save
      onBack()
    } catch (error) {
      console.error('Error marking onboarding as ready:', error)
      // TODO: Show error toast
    } finally {
      setSaving(false)
    }
  }

  const handlePublishArtist = async () => {
    if (!onPublishArtist) return
    
    setPublishError(null)
    setSaving(true)
    setPublishProgressOpen(true)
    try {
      const updates = {
        ...editedData,
        // Only include selected flashes and tattoo images
        flashes: editedData.flashes,
        tattooImageUrls: editedData.tattooImageUrls
      }

      await onPublishArtist(updates)
      
      // Navigate back to list view after successful publish
      onBack()
    } catch (error) {
      console.error('Error publishing artist:', error)
      const msg = error instanceof Error ? error.message : 'Failed to publish artist. Please try again.'
      setPublishError(msg)
    } finally {
      setPublishProgressOpen(false)
      setSaving(false)
    }
  }

  const hasChanges = JSON.stringify(editedData) !== JSON.stringify({
    displayName: onboarding.displayName,
    firstName: onboarding.firstName || '',
    lastName: onboarding.lastName || '',
    bio: onboarding.bio,
    location: onboarding.location || '',
    lat: onboarding.lat,
    lon: onboarding.lon,
    profileImageUrl: onboarding.profileImageUrl,
    backdropUrl: onboarding.backdropUrl || '',
    tattooImageUrls: onboarding.tattooImageUrls || [],
    flashes: onboarding.flashes || []
  }) || profileImageFile !== null || bannerImageFile !== null

  const canMarkReady = (editedData.tattooImageUrls?.length || 0) >= 10 && 
                      editedData.displayName?.trim() && 
                      editedData.bio?.trim()

  const igHandle =
    onboarding.displayName.startsWith('@') ? onboarding.displayName : `@${onboarding.displayName}`

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground mx-auto mb-4"></div>
          <p className="text-foreground">Loading onboarding details...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 relative">
      {publishProgressOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-background/85 backdrop-blur-sm p-4"
          role="alertdialog"
          aria-busy="true"
          aria-live="polite"
          aria-label="Publishing artist"
        >
          <div className="w-full max-w-md rounded-xl border bg-card p-6 shadow-lg text-center space-y-4">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" aria-hidden />
            <div className="space-y-2">
              <p className="text-lg font-semibold">Publishing artist</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{publishStatusMessage}</p>
              <p className="text-xs text-muted-foreground tabular-nums">Elapsed {publishElapsedLabel}</p>
            </div>
          </div>
        </div>
      )}

      {publishError && (
        <div
          className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
          role="alert"
        >
          <span className="min-w-0 break-words">{publishError}</span>
          <Button type="button" variant="outline" size="sm" className="shrink-0 border-destructive/40" onClick={() => setPublishError(null)}>
            Dismiss
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            className="flex shrink-0 items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to List
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold flex flex-wrap items-center gap-2">
              <span className="truncate">{igHandle}</span>
              <Badge className={getStatusColor(onboarding.status)} variant="secondary">
                {formatStatus(onboarding.status)}
              </Badge>
            </h1>
            <div className="mt-3 flex flex-wrap items-stretch gap-3">
              <div className="min-w-[min(100%,14rem)] flex-1 rounded-md border bg-muted/40 px-3 py-2 sm:max-w-xs">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                  Invited by
                </p>
                <InvitingArtistAttribution
                  artists={artistsForLookup}
                  invitingArtistId={onboarding.invitingArtist}
                />
              </div>
              {onboarding.code && (
                <div className="flex items-center">
                  <Badge variant="outline" className="font-mono h-fit">
                    Code: {onboarding.code}
                  </Badge>
                </div>
              )}
              {onboarding.createdAt && (
                <div className="flex items-center">
                  <span className="text-sm text-muted-foreground">
                    Created {onboarding.createdAt.toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          
          {onboarding.status === 'waiting_for_review' && onPublishArtist ? (
            <Button
              onClick={handlePublishArtist}
              disabled={saving}
              className="flex items-center gap-2"
            >
              <Rocket className="w-4 h-4" />
              {saving ? 'Publishing...' : 'Publish Artist'}
            </Button>
          ) : (
            <Button
              onClick={handleMarkReady}
              disabled={!canMarkReady || saving || onboarding.status === 'ready_for_artist'}
              className="flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              {saving ? 'Processing...' : 'Ready for Artist'}
            </Button>
          )}
        </div>
      </div>

      {/* Artist Information */}
      <Card>
        <CardHeader>
          <CardTitle>Artist Information</CardTitle>
          <CardDescription>
            Edit the basic information about this artist
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Profile Image */}
            <div className="space-y-2">
              <Label>Profile Image</Label>
              <div className="flex items-center gap-4">
                <img
                  src={profileImagePreview || editedData.profileImageUrl}
                  alt={editedData.displayName}
                  className="w-16 h-16 rounded-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement
                    target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(editedData.displayName || 'Artist')}&background=random`
                  }}
                />
                <div className="flex-1 space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProfileImageChange}
                    className="hidden"
                    id="profile-image-upload"
                    disabled={profileUploading}
                  />
                  <label
                    htmlFor="profile-image-upload"
                    className={`inline-flex items-center gap-2 px-3 py-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md cursor-pointer text-sm ${
                      profileUploading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Upload className="w-4 h-4" />
                    {profileUploading ? 'Uploading...' : 'Upload New Image'}
                  </label>
                  {editedData.profileImageUrl && !profileUploadError && (
                    <p className="text-xs text-green-600">
                      ✓ Profile image uploaded successfully
                    </p>
                  )}
                  {profileUploadError && (
                    <p className="text-xs text-destructive">{profileUploadError}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Banner Image */}
            <div className="space-y-2">
              <Label>Banner Image</Label>
              <div className="space-y-3">
                {/* Banner Preview */}
                <div className="relative w-full h-24 rounded-lg overflow-hidden border-2 border-border bg-gradient-to-r from-purple-500 to-pink-500">
                  {(bannerImagePreview || editedData.backdropUrl) && (
                    <img
                      src={bannerImagePreview || editedData.backdropUrl}
                      alt="Banner preview"
                      className="w-full h-full object-cover"
                    />
                  )}
                  {!bannerImagePreview && !editedData.backdropUrl && (
                    <div className="absolute inset-0 flex items-center justify-center text-white">
                      <div className="text-center">
                        <ImageIcon className="w-6 h-6 mx-auto mb-1 opacity-70" />
                        <p className="text-xs opacity-70">Banner preview</p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Upload Controls */}
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBannerImageChange}
                    className="hidden"
                    id="banner-image-upload"
                    disabled={bannerUploading}
                  />
                  <label
                    htmlFor="banner-image-upload"
                    className={`inline-flex items-center gap-2 px-3 py-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md cursor-pointer text-sm ${
                      bannerUploading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Upload className="w-4 h-4" />
                    {bannerUploading ? 'Uploading...' : 'Upload Banner'}
                  </label>
                  
                  <p className="text-xs text-muted-foreground">
                    Photo of artist during session or studio. You'll be able to crop it to the perfect size.
                  </p>
                  
                  {editedData.backdropUrl && !bannerUploadError && (
                    <p className="text-xs text-green-600">
                      ✓ Banner image uploaded and cropped
                    </p>
                  )}
                  
                  {bannerUploadError && (
                    <p className="text-xs text-destructive">{bannerUploadError}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Instagram Username (Read-only) */}
            <div className="space-y-2">
              <Label htmlFor="displayName">Instagram Username</Label>
              <Input
                id="displayName"
                value={editedData.displayName || ''}
                readOnly
                className="bg-muted cursor-not-allowed"
              />
              <p className="text-xs text-muted-foreground">
                Instagram username (cannot be changed)
              </p>
            </div>
          </div>

          {/* Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={editedData.firstName || ''}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                placeholder="First name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={editedData.lastName || ''}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                placeholder="Last name"
              />
              <p className="text-xs text-muted-foreground">
                Only first letter shown publicly
              </p>
            </div>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <AddressAutocomplete
              value={editedData.location || ''}
              onChange={(value) => handleInputChange('location', value)}
              onSelect={handleAddressSelect}
              placeholder="Start typing the studio address..."
            />
            <p className="text-xs text-muted-foreground">
              Start typing the address and select from suggestions for accurate location data.
            </p>
            {geocodingError && (
              <p className="text-xs text-destructive">{geocodingError}</p>
            )}
            {editedData.lat && editedData.lon && (
              <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
                <strong>Coordinates:</strong> {editedData.lat.toFixed(4)}, {editedData.lon.toFixed(4)}
              </div>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="bio">Description *</Label>
            <Textarea
              id="bio"
              value={editedData.bio || ''}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              placeholder="Artist biography"
              rows={4}
            />
            <p className="text-xs text-muted-foreground">
              Tip: You can write a lot longer description than on Instagram. Users then have an option to read more!
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Image Gallery */}
      <TattooImageGallery
        postUrls={onboarding.postUrls}
        selectedTattooUrls={editedData.tattooImageUrls || []}
        onSelectionChange={handleTattooSelectionChange}
        onboardingId={onboarding.id}
      />

      {/* Flash Gallery - Show for waiting_for_review status */}
      {onboarding.status === 'waiting_for_review' && onboarding.flashes && onboarding.flashes.length > 0 && (
        <FlashGallery
          flashes={onboarding.flashes}
          selectedFlashIds={selectedFlashIds}
          onSelectionChange={handleFlashSelectionChange}
        />
      )}

      {/* Requirements Check */}
      <Card>
        <CardHeader>
          <CardTitle>Ready for Artist Requirements</CardTitle>
          <CardDescription>
            All requirements must be met before marking as ready for artist
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className={`flex items-center gap-2 ${editedData.displayName?.trim() ? 'text-green-600' : 'text-red-600'}`}>
              <CheckCircle className="w-4 h-4" />
              Instagram username is present
            </div>
            <div className={`flex items-center gap-2 ${editedData.firstName?.trim() ? 'text-blue-600' : 'text-gray-500'}`}>
              <CheckCircle className="w-4 h-4" />
              First name is filled (optional - artist can add later)
            </div>
            <div className={`flex items-center gap-2 ${editedData.lastName?.trim() ? 'text-blue-600' : 'text-gray-500'}`}>
              <CheckCircle className="w-4 h-4" />
              Last name is filled (optional - artist can add later)
            </div>
            <div className={`flex items-center gap-2 ${editedData.bio?.trim() ? 'text-green-600' : 'text-red-600'}`}>
              <CheckCircle className="w-4 h-4" />
              Description is filled
            </div>
            <div className={`flex items-center gap-2 ${editedData.location?.trim() ? 'text-blue-600' : 'text-gray-500'}`}>
              <CheckCircle className="w-4 h-4" />
              Location is filled (optional - artist can add later)
            </div>
            <div className={`flex items-center gap-2 ${(editedData.tattooImageUrls?.length || 0) >= 10 ? 'text-green-600' : 'text-red-600'}`}>
              <CheckCircle className="w-4 h-4" />
              At least 10 tattoo images selected ({editedData.tattooImageUrls?.length || 0}/10 minimum, goal: 20)
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Banner Cropper Modal */}
      {showBannerCropper && bannerImageFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <BannerImageCropper
            imageFile={bannerImageFile}
            onCropComplete={handleBannerCropComplete}
            onCancel={handleBannerCropCancel}
          />
        </div>
      )}
    </div>
  )
}
